import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import CloseButton from "web/react/components/buttons/close-button/close-button";
import EmailCaptureForm from "web/react/components/email-capture-form/email-capture-form";
import JSONLD from "web/react/components/json-ld/json-ld";
import analytics from "web/script/analytics/analytics";
import facebookEvents from "web/script/analytics/facebook-events";
import { gettext } from "web/script/modules/django-i18n";
import requester from "web/script/modules/requester";
import userProfiler from "web/script/modules/userprofiler";
import storage from "web/script/utils/storage";
import url from "web/script/utils/url";
import styles from "./homepage-overlay.css";

function HomepageOverlay({
    cookiePolicyLink,
    privacyPolicyLink,
    termsAndConditionsLink,
    refreshTest,
    signupStyle,
    isEU,
    signInLink,
}) {
    const appElement = useRef(null);
    const [emailAddress, setEmailAddress] = useState("");
    const [formErrors, setFormErrors] = useState(undefined);
    const [marketingEmails, setMarketingEmails] = useState(false);
    const [overlayOpen, setOverlayOpen] = useState(true);
    const [privacyPolicy, setPrivacyPolicy] = useState(false);

    useEffect(() => {
        if (
            storage.get("homepageOverlayShown") === true ||
            userProfiler.getHomepageOverlayShown() === true
        ) {
            setOverlayOpen(false);
        } else {
            analytics.event("signup_hp_wall", "open_signup_form", "open_overlay", true);
            if (!refreshTest) {
                userProfiler.setHomepageOverlayShown();
                storage.set("homepageOverlayShown", true);
            }
        }
        Modal.setAppElement(appElement.current);
    }, [refreshTest]);

    function changeEmail(event) {
        setEmailAddress(event.target.value);
    }

    function handleLegalChange(privacyPolicyChecked, marketingEmailsChecked) {
        if (privacyPolicy !== privacyPolicyChecked || marketingEmails !== marketingEmailsChecked) {
            setPrivacyPolicy(privacyPolicyChecked);
            setMarketingEmails(marketingEmailsChecked);
        }
    }

    async function onSubmit(event) {
        event.preventDefault();
        analytics.event("signup_hp_wall", "click_signup_button", "signup_button");
        const body = {
            email: emailAddress,
            marketing_emails: marketingEmails,
            privacy_policy: privacyPolicy,
        };

        const parsedURL = url.parse(window.location);
        const nextParam = parsedURL.path;

        let jsonData = undefined;
        const queryParams = url
            .toQueryString({
                type: "normal_hp_wall",
                next: nextParam,
                email_capture: "true",
            })
            .toString();
        try {
            jsonData = await requester.post(`/account/register/?${queryParams}`, body);
        } catch (e) {
            console.error(e);
            setFormErrors({ other: [gettext("account.login.with_magic_link.error_title")] });
            return;
        }
        if (jsonData && jsonData.success) {
            analytics.tagEvent(
                analytics.TAG_NETWORKS.FACEBOOK,
                facebookEvents.REGISTRATION_COMPLETE
            );
            if (refreshTest) {
                userProfiler.setHomepageOverlayShown();
                storage.set("homepageOverlayShown", true);
            }
            window.location = jsonData.next;
        } else {
            const { errors } = jsonData.data;
            setFormErrors(errors);
        }
    }

    function handleClose() {
        setOverlayOpen(false);
    }

    function onSocialClick(authProvider) {
        event.preventDefault();
        // signup_facebook, signup_google
        analytics.event("signup_hp_wall", "click_social_sign_up", `signup_${authProvider}`);

        if (privacyPolicy !== true || marketingEmails !== true) {
            setFormErrors({
                privacy_policy: [gettext("account.register.user_must_tick_both_boxes")],
            });
            return;
        }

        if (refreshTest) {
            userProfiler.setHomepageOverlayShown();
            storage.set("homepageOverlayShown", true);
        }

        // start with the current URL (including all query parameters)
        const parsedURL = url.parse(window.location);
        const path = parsedURL.path;
        const params = url.toQueryString(parsedURL.searchParams);
        const nextParam = `${path}?${params}`;
        const searchTerm = parsedURL.searchParams.get("q");
        const searchGender =
            parsedURL.searchParams.get("gender") || parsedURL.searchParams.get("meta_gender");
        const reason = parsedURL.searchParams.get("reason");
        const type = parsedURL.searchParams.get("type");
        // update the path to point to the correct auth provider

        parsedURL.path = `/social/login/${authProvider}/`;
        parsedURL.searchParams.set("next", nextParam);

        parsedURL.searchParams.set("isLogin", false);
        parsedURL.searchParams.set("marketingEmails", marketingEmails);
        parsedURL.searchParams.set("reason", reason);

        parsedURL.searchParams.set("authType", type);
        parsedURL.searchParams.set("searchQuery", searchTerm);
        parsedURL.searchParams.set("searchGender", searchGender);

        // do the redirect
        window.location = url.unparse(parsedURL);
    }

    let title = gettext("homepage.sign_up.header_shop_thousands_brands");
    let submitText = "Continue to Lyst";
    const showSocial = ["all", "social"].includes(signupStyle);
    const hideEmail = signupStyle == "social";

    const jsonLD = {
        "@context": "https://schema.org/",
        "@type": "WebPage",
        isAccessibleForFree: "False",
        hasPart: {
            "@type": "WebPageElement",
            isAccessibleForFree: "False",
            cssSelector: ".site-wrapper",
        },
    };

    const queryParams = url
        .toQueryString({
            type: "normal_hp_wall",
        })
        .toString();
    const signInLinkProps = signInLink + "?" + queryParams;

    const closeButton = (
        <div className={styles.close}>
            <CloseButton onClick={handleClose} className={styles.closeButton} />
        </div>
    );

    const form = (
        <div className={styles.captureForm}>
            <div className={styles.headerImage}>
                <div className={styles.headerTitle}>
                    {gettext("homepage.sign_up.welcome_title")}
                </div>
                {isEU && closeButton}
            </div>
            <div className={styles.contentBox}>
                <div className={styles.header}>
                    <h3 className={styles.title}>{title}</h3>
                </div>
                <EmailCaptureForm
                    captureType={"signup_hp_wall"}
                    cookiePolicyLink={cookiePolicyLink}
                    emailAddress={emailAddress}
                    emailLabelText={gettext(
                        "account.register.return_lead.recently_viewed_items.email_label"
                    )}
                    formErrors={formErrors}
                    onEmailChange={changeEmail}
                    onLegalChange={handleLegalChange}
                    onSubmit={onSubmit}
                    privacyPolicyLink={privacyPolicyLink}
                    submitText={submitText}
                    termsAndConditionsLink={termsAndConditionsLink}
                    shortEUPolicy={true}
                    hideEmail={hideEmail}
                    showSocial={showSocial}
                    onSocialClick={onSocialClick}
                    className={styles.form}
                    signInLinkProps={signInLinkProps}
                />
            </div>
        </div>
    );

    return (
        <div id="homepage-email-capture-overlay" ref={appElement}>
            <Modal
                appElement={document.body}
                isOpen={overlayOpen}
                className={styles.modal}
                overlayClassName={styles.overlay}
            >
                {form}
            </Modal>
            <JSONLD JSONLD={jsonLD} />
        </div>
    );
}

export default HomepageOverlay;

HomepageOverlay.propTypes = {
    cookiePolicyLink: PropTypes.string.isRequired,
    privacyPolicyLink: PropTypes.string.isRequired,
    termsAndConditionsLink: PropTypes.string.isRequired,
    refreshTest: PropTypes.bool,
    signupStyle: PropTypes.string.isRequired,
    isEU: PropTypes.bool,
    signInLink: PropTypes.string,
};
